import { STREICH_FARBEN, type PdfRect, type StreichEintrag } from "./pdfBearbeitung";

/**
 * Durchstreichungen (T-363) als echte `/StrikeOut`-Anmerkungen ins PDF.
 *
 * PDF.js kann eine Durchstreichung zwar anzeigen, aber nicht anlegen — der
 * Editor schreibt sie deshalb beim Speichern selbst in die Bytes aus
 * `saveDocument()`, VOR der Verzweigung „als Bild“/„als PDF“. So gilt sie für
 * beide Wege, und der Server bleibt unberührt.
 *
 * Jede Anmerkung bringt ihr Aussehen (`/AP /N`) mit: ein gerader Strich durch
 * die Mitte. Ohne AP zeichnet nicht jedes Programm eine StrikeOut gleich (oder
 * gar nicht) — mit AP sieht sie überall so aus wie im Editor. Der durchgestrichene
 * Text selbst steht NICHT in der Anmerkung (`/Contents` fehlt bewusst).
 *
 * Der Text darunter bleibt unangetastet und lesbar.
 */

/** Zahl fürs Content-Stream-Format — kurz, ohne Exponent. */
const z = (n: number) => (Math.round(n * 1000) / 1000).toString();

/** PDF-Datum `D:JJJJMMTTHHmmSSZ`. */
const pdfDatum = (d: Date) =>
    `D:${d.getUTCFullYear()}${[d.getUTCMonth() + 1, d.getUTCDate(), d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds()]
        .map((n) => String(n).padStart(2, "0"))
        .join("")}Z`;

/** Umriss der Anmerkung: Rechteck und Strich samt halber Strichstärke. */
export function anmerkungsRechteck(e: StreichEintrag): PdfRect {
    const [x1, y1, x2, y2] = e.linie;
    const h = e.staerke / 2;
    return [
        Math.min(e.rect[0], x1 - h, x2 - h),
        Math.min(e.rect[1], y1 - h, y2 - h),
        Math.max(e.rect[2], x1 + h, x2 + h),
        Math.max(e.rect[3], y1 + h, y2 + h),
    ];
}

/** QuadPoints nach PDF-Spezifikation: oben links, oben rechts, unten links, unten rechts. */
export function quadPoints(r: PdfRect): number[] {
    const [x0, y0, x1, y1] = r;
    return [x0, y1, x1, y1, x0, y0, x1, y0];
}

/** Inhalt des Aussehens: ein Strich in Farbe und Stärke. */
export function aussehen(e: StreichEintrag): string {
    const [r, g, b] = STREICH_FARBEN[e.farbe].rgb;
    const [x1, y1, x2, y2] = e.linie;
    return `${z(r)} ${z(g)} ${z(b)} RG ${z(e.staerke)} w 0 J ${z(x1)} ${z(y1)} m ${z(x2)} ${z(y2)} l S`;
}

/**
 * Schreibt die Durchstreichungen in das PDF. Leere Liste → dieselben Bytes,
 * ohne pdf-lib anzufassen (kein Risiko für Dateien, die pdf-lib nicht mag).
 * Vorhandene Anmerkungen der Seiten bleiben erhalten.
 */
export async function streichungenSchreiben(
    bytes: Uint8Array,
    liste: readonly StreichEintrag[],
    jetzt: Date = new Date(),
): Promise<Uint8Array> {
    if (liste.length === 0) return bytes;
    const { PDFArray, PDFDocument, PDFName, PDFString } = await import("pdf-lib");
    const doc = await PDFDocument.load(bytes, { updateMetadata: false });
    const ctx = doc.context;
    const seiten = doc.getPages();
    const datum = PDFString.of(pdfDatum(jetzt));
    for (const e of liste) {
        const seite = seiten[e.seite];
        if (!seite) continue;
        const rect = anmerkungsRechteck(e);
        const farbe = STREICH_FARBEN[e.farbe].rgb;
        const ap = ctx.stream(aussehen(e), {
            Type: "XObject",
            Subtype: "Form",
            BBox: rect,
            Resources: {},
        });
        const annot = ctx.obj({
            Type: "Annot",
            Subtype: "StrikeOut",
            Rect: rect,
            QuadPoints: quadPoints(e.rect),
            C: farbe,
            CA: 1,
            // Print: auch im Ausdruck.
            F: 4,
            NM: PDFString.of(`kmvs-strich-${e.id}`),
            M: datum,
            AP: { N: ctx.register(ap) },
        });
        annot.set(PDFName.of("P"), seite.ref);
        const ref = ctx.register(annot);
        const annots = seite.node.lookupMaybe(PDFName.of("Annots"), PDFArray);
        if (annots) annots.push(ref);
        else seite.node.set(PDFName.of("Annots"), ctx.obj([ref]));
    }
    return doc.save({ useObjectStreams: false });
}
